import api from "../api";
import React, { useState, useEffect, useContext } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm, Controller } from "react-hook-form";
import {
  Alert,
  TextField,
  Button,
  RadioGroup,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Grid,
  Typography,
  Radio,
  Box,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import { ArrowBack } from "@mui/icons-material";
import { Link, useNavigate, useParams } from "react-router-dom";
import { salonUpdateSchema } from "./validation";
import { DeadlineContext } from "../App"

const SCORE_VALUES = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

// Entonnoir du bilan quantitatif, dans l'ordre où les valeurs se contraignent.
const QUANTI_FIELDS = [
  { name: "ProspectsMetN__c", previous: "ProspectsMetN1__c", label: "Prospects rencontrés" },
  { name: "PotentielCandidateN__c", previous: "PotentialCandidateN1__c", label: "Candidats potentiels" },
  { name: "DCN__c", label: "Candidats" },
  { name: "RegisteredN__c", previous: "RegisteredN1__c", label: "Inscrits" },
];

const SCORE_FIELDS = [
  { name: "LocationQuality__c", label: "Qualité de l'emplacement" },
  { name: "LeadQuality__c", label: "Qualité des prospects" },
  { name: "TrafficQuality__c", label: "Qualité du trafic" },
  { name: "QualityOfTheReception__c", label: "Qualité de l'organisation" },
];

const UpdateFicheSalon = () => {

  const navigate = useNavigate();
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const { id: salonId } = useParams();
  const [salonData, setSalonData] = useState({});
  const [loading, setLoading] = useState(true);
  const deadline = new Date(useContext(DeadlineContext));
  // La fiche salon mémorise la section à ouvrir ; on retombe sur le
  // quantitatif si la valeur est absente ou inconnue.
  const storedSection = localStorage.getItem('section');
  const section = salonUpdateSchema[storedSection] ? storedSection : "quantitatif";


  const {
    handleSubmit,
    control,
    watch,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(salonUpdateSchema[section]),
  });


  useEffect(() => {
    const fetchSalon = async () => {
      setLoading(true);
      try {
        const response = await api.get("/events/getFicheSalon", {
          params: {
            salonId: salonId,
          },
        });
        if (response.data.success) {
          setSalonData(response.data.data[0]);
          reset({
            ProspectsMetN__c: response.data.data[0].ProspectsMetN__c,
            PotentielCandidateN__c: response.data.data[0].PotentielCandidateN__c,
            DCN__c: response.data.data[0].DCN__c,
            RegisteredN__c: response.data.data[0].RegisteredN__c,
            LocationQuality__c: response.data.data[0].LocationQuality__c,
            LeadQuality__c: response.data.data[0].LeadQuality__c,
            TrafficQuality__c: response.data.data[0].TrafficQuality__c,
            QualityOfTheReception__c: response.data.data[0].QualityOfTheReception__c,
            Improvments__c: response.data.data[0].Improvments__c,
            DesiredArea__c: response.data.data[0].DesiredArea__c,
            Observations__c: response.data.data[0].Observations__c,
            DesiredLocation__c: response.data.data[0].DesiredLocation__c,
            ToRedo__c: response.data.data[0].ToRedo__c,
          })
        } else {
          console.error("Failed to fetch salon details:", response.data.error);
        }
      } catch (err) {
        console.error("An error occurred:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchSalon();
  }, [salonId, setSalonData, reset]);

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  const salonName = salonData.Name;
  const onSubmit = async (data) => {
    setMessage("");
    setSubmitting(true);
    try {
      const response = await api.patch("/events/updateFicheSalon", {
        salonId: salonId,
        data: data,
      });
      if (response.data.success) {
        navigate(`/FicheSalon/${salonId}`);
      } else {
        setMessage(response.data.error || response.data.message);
      }
    } catch (error) {
      console.error("Error in onSubmit:", error);
      setMessage(
        "Une erreur est survenue lors de la mise à jour de la fiche salon. Veuillez réessayer."
      );
    } finally {
      setSubmitting(false);
    }
  };
  const currentDate = new Date();
  const canEdit = currentDate <= deadline;

  let updateMessage = '';
  let updateSeverity = 'info';
  if (currentDate < deadline) {
    updateMessage = `Vous pouvez modifier les données de ce salon jusqu'au ${deadline.toLocaleDateString()}.`;
  } else if (currentDate.toDateString() === new Date(deadline).toDateString()) {
    updateMessage = "C'est le dernier jour pour modifier les données des salons.";
    updateSeverity = 'warning';
  } else if (currentDate > deadline) {
    updateMessage = `Les données des salons ne sont plus modifiables depuis le ${deadline.toLocaleDateString()}.`;
    updateSeverity = 'warning';
  }

  const quantiFields = (
    <>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Chaque valeur ne peut pas dépasser la précédente : prospects ≥ candidats potentiels ≥ candidats ≥ inscrits.
      </Typography>
      <Grid container spacing={3}>
        {QUANTI_FIELDS.map(({ name, previous, label }) => {
          const previousValue = previous ? salonData[previous] : null;
          return (
            <Grid item xs={12} sm={6} key={name}>
              <Controller
                name={name}
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    value={field.value ?? ""}
                    type="number"
                    inputProps={{ min: 0 }}
                    error={!!errors[name]}
                    helperText={
                      errors?.[name]?.message ||
                      (previousValue !== null && previousValue !== undefined ? `N-1 : ${previousValue}` : " ")
                    }
                    label={label}
                    fullWidth
                  />
                )}
              />
            </Grid>
          );
        })}
      </Grid>
    </>
  );

  const qualiFields = (
    <Stack spacing={4}>
      <Box>
        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
          Notes sur 10
        </Typography>
        <Stack spacing={2.5}>
          {SCORE_FIELDS.map(({ name, label }) => (
            <Controller
              key={name}
              name={name}
              control={control}
              render={({ field }) => (
                <FormControl error={!!errors[name]}>
                  <FormLabel sx={{ mb: 1, color: "text.primary" }}>{label}</FormLabel>
                  <ToggleButtonGroup
                    exclusive
                    size="small"
                    color="primary"
                    value={field.value === null || field.value === undefined ? null : Number(field.value)}
                    // Un clic sur la note déjà choisie la désélectionnerait :
                    // on garde la valeur courante.
                    onChange={(_, value) => value !== null && field.onChange(value)}
                    aria-label={label}
                    sx={{ flexWrap: "wrap" }}
                  >
                    {SCORE_VALUES.map((v) => (
                      <ToggleButton key={v} value={v} sx={{ minWidth: 40 }}>
                        {v}
                      </ToggleButton>
                    ))}
                  </ToggleButtonGroup>
                  {errors[name] && <FormHelperText>{errors[name].message}</FormHelperText>}
                </FormControl>
              )}
            />
          ))}
        </Stack>
      </Box>

      <Divider />

      <Stack spacing={3}>
        <Typography variant="subtitle1" fontWeight={600}>
          Retours
        </Typography>
        <Controller
          name="Observations__c"
          control={control}
          rules={{ required: true }}
          render={({ field }) => (
            <TextField
              {...field}
              value={field.value ?? ""}
              label="Observations"
              multiline
              minRows={3}
              fullWidth
            />
          )}
        />
        <Controller
          name="Improvments__c"
          control={control}
          rules={{ required: true }}
          render={({ field }) => (
            <TextField
              {...field}
              value={field.value ?? ""}
              label="Axes d'amélioration"
              multiline
              minRows={3}
              fullWidth
            />
          )}
        />
      </Stack>

      <Divider />

      <Stack spacing={3}>
        <FormControl>
          <FormLabel sx={{ color: "text.primary", fontWeight: 600 }}>
            À refaire l'an prochain ?
          </FormLabel>
          <Controller
            name="ToRedo__c"
            control={control}
            render={({ field }) => (
              <RadioGroup {...field} value={field.value ?? ""} row>
                <FormControlLabel value="Oui" control={<Radio />} label="Oui" />
                <FormControlLabel value="Non" control={<Radio />} label="Non" />
              </RadioGroup>
            )}
          />
        </FormControl>
        {watch("ToRedo__c") === "Oui" && (
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <Controller
                name="DesiredArea__c"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    value={field.value ?? ""}
                    error={!!errors.DesiredArea__c}
                    helperText={errors?.DesiredArea__c?.message}
                    label="Superficie souhaitée"
                    fullWidth
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="DesiredLocation__c"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    value={field.value ?? ""}
                    label="Emplacement souhaité"
                    fullWidth
                  />
                )}
              />
            </Grid>
          </Grid>
        )}
      </Stack>
    </Stack>
  );

  return (
    <Box sx={{ maxWidth: 800, mx: "auto", px: { xs: 2, md: 4 }, py: 4 }}>
      <Button
        component={Link}
        to={`/FicheSalon/${salonId}`}
        startIcon={<ArrowBack />}
        sx={{ mb: 2, ml: -1 }}
      >
        Retour à la fiche
      </Button>

      <Typography variant="h4" component="h1">
        {section === "quantitatif" ? "Bilan quantitatif" : "Bilan qualitatif"}
      </Typography>
      <Typography variant="subtitle1" color="text.secondary" gutterBottom>
        {salonName}
      </Typography>

      {updateMessage && (
        <Alert severity={updateSeverity} sx={{ my: 3 }}>
          {updateMessage}
        </Alert>
      )}

      <Paper variant="outlined" component="form" onSubmit={handleSubmit(onSubmit)} sx={{ p: { xs: 2, sm: 4 } }}>
        {section === "quantitatif" ? quantiFields : qualiFields}

        {message && (
          <Alert severity="error" sx={{ mt: 3 }}>
            {message}
          </Alert>
        )}

        {canEdit && (
          <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ mt: 4 }}>
            <Button variant="outlined" component={Link} to={`/FicheSalon/${salonId}`}>
              Annuler
            </Button>
            <Button type="submit" variant="contained" disabled={submitting}>
              {submitting ? "Enregistrement…" : "Enregistrer"}
            </Button>
          </Stack>
        )}
      </Paper>
    </Box>
  );
};

export default UpdateFicheSalon;
